import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { adminLogin } from "@/lib/adminApi";
import { SITE } from "@/data/site";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg ? e.msg : "")).join(" ");
  if (detail.msg) return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(email, password);
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-brand-bg border border-brand-border rounded-xl px-5 py-4 text-brand-navy placeholder:text-brand-muted/60 focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition";

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-navy px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md bg-white rounded-2xl border border-brand-border p-8 lg:p-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <img src={SITE.logo} alt="Arivu Finserve" className="h-11 w-11 rounded-md object-contain" />
          <div>
            <p className="font-serif text-xl text-brand-navy leading-none">Arivu Finserve</p>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-green mt-1">Admin</p>
          </div>
        </div>

        <h1 className="font-serif text-3xl text-brand-navy flex items-center gap-2">
          <Lock className="h-6 w-6 text-brand-green" /> Enquiry Inbox
        </h1>
        <p className="text-brand-muted font-light mt-2">Sign in to view submitted enquiries.</p>

        <form onSubmit={submit} data-testid="admin-login-form" className="mt-8 space-y-4">
          <input data-testid="admin-email" className={field} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <input data-testid="admin-password" className={field} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy text-white text-sm font-medium px-8 py-4 hover:bg-brand-green transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in..." : "Sign in"} <ArrowRight className="h-4 w-4" />
          </button>
        </form>
      </motion.div>
    </div>
  );
}
